import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import HelpTooltip from 'help/HelpTooltip';
import {SearchRegion} from 'molbio/biological-search/SearchDNAData';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import ControlledNumericInput from 'ui-library/inputs/ControlledNumericInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import ToggleGroup from 'ui-library/ToggleGroup';
import * as keyboard from 'util/Keyboard';
import {ElementKeyboardEvent, ElementMouseEvent} from 'util/Types';

export enum SequenceSearchType {
  BASES = 'Bases',
  AMINO_ACIDS = 'Amino Acids',
}

export interface SearchSequenceAdvancedOptionsProps {
  searchType: SequenceSearchType;
  onChangeSearchType: (searchType: SequenceSearchType) => void;
  searchRegion: SearchRegion;
  onChangeSearchRegion: (region: SearchRegion) => void;
  mismatchesAllowed: number | null;
  onChangeMismatchesAllowed: (mismatches: number | null) => void;
  onClickORFSettings: () => void;
  onToggleSettings: (e: ElementMouseEvent) => void;
  isBigSelection: boolean;
  isBigSequence: boolean;
}

export function SearchSequenceAdvancedOptions(props: SearchSequenceAdvancedOptionsProps): JSX.Element {
  function handleMismatchesAllowedChange(mismatches: number | null): void {
    const nonNegativeMismatches = mismatches && mismatches > 0 ? mismatches : 0;
    props.onChangeMismatchesAllowed(nonNegativeMismatches);
  }

  function incrementMismatchesAllowed(): void {
    handleMismatchesAllowedChange((props.mismatchesAllowed || 0) + 1);
  }

  function decrementMismatchesAllowed(): void {
    handleMismatchesAllowedChange((props.mismatchesAllowed || 0) - 1);
  }

  return (
    <>
      <div className="settings-header">
        <span>Advanced Settings</span>
        <Button buttonStyle={ButtonStyle.LINK} text="Close" onClick={props.onToggleSettings} />
      </div>
      <div className="form-horizontal">
        <div>
          <FieldLabel className="control-label" label="Search" />
        </div>
        <div>
          <ToggleGroup
            options={[
              {
                id: SequenceSearchType.BASES,
                buttonProps: {text: SequenceSearchType.BASES},
              },
              {
                id: SequenceSearchType.AMINO_ACIDS,
                buttonProps: {text: SequenceSearchType.AMINO_ACIDS},
              },
            ]}
            selection={props.searchType}
            onChangeSelection={(searchType: SequenceSearchType) => {
              props.onChangeSearchType(searchType);
            }}
            size={ButtonSize.SMALL}
          />
        </div>
        <div>
          <FieldLabel
            className={classNames('control-label', css(styles.smallMarginRight))}
            label="ORF Region"
          />
          <HelpTooltip
            text={
              props.isBigSequence
                ? 'Restricting search to a region is disabled on large sequences.'
                : 'Restricting search to ORFs requires that each result occurs in-frame with an ORF.'
            }
            container="body"
          />
          {props.searchRegion === 'orfs' && (
            <Button
              buttonStyle={ButtonStyle.UNBUTTON}
              icon="wrench"
              className={css(styles.wrenchIcon)}
              onClick={props.onClickORFSettings}
            />
          )}
        </div>
        <div>
          <CheckboxInput
            id="restrictToORF"
            checked={props.searchRegion === 'orfs' && !props.isBigSequence}
            onChange={({target: {checked}}) => {
              const region = checked ? 'orfs' : 'all';
              props.onChangeSearchRegion(region);
            }}
            label="Only show results in-frame with an ORF"
            disabled={props.isBigSequence}
          />
        </div>
        {props.searchType === SequenceSearchType.BASES && (
          <>
            <div>
              <FieldLabel
                className={classNames('control-label', css(styles.smallMarginRight))}
                label="Mismatches allowed"
              />
              <HelpTooltip
                text={
                  props.isBigSelection
                    ? 'Quick search with mismatches is disabled on large sequences.'
                    : 'Maximum number of mismatches tolerated between search query and result.'
                }
                container="body"
              />
            </div>
            <div className="row">
              <div className={classNames('col col-xs-5', css(styles.flex))}>
                <Button
                  icon="minus"
                  buttonStyle={ButtonStyle.TERTIARY_BLUE}
                  size={ButtonSize.SMALL}
                  onClick={decrementMismatchesAllowed}
                  disabled={props.isBigSelection}
                />
                <ControlledNumericInput
                  min={0}
                  onChange={({target: {numericValue}}) => {
                    handleMismatchesAllowedChange(numericValue);
                  }}
                  onKeyDown={(e: ElementKeyboardEvent) => {
                    if (keyboard.isUpKey(e)) {
                      incrementMismatchesAllowed();
                    } else if (keyboard.isDownKey(e)) {
                      decrementMismatchesAllowed();
                    }
                  }}
                  placeholder="0"
                  value={props.mismatchesAllowed}
                  required={false}
                  validateInteger={true}
                  className={css(styles.numberInput)}
                  isDisabled={props.isBigSelection}
                />
                <Button
                  icon="plus"
                  buttonStyle={ButtonStyle.TERTIARY_BLUE}
                  size={ButtonSize.SMALL}
                  onClick={incrementMismatchesAllowed}
                  disabled={props.isBigSelection}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

const styles = StyleSheet.create({
  flex: {
    display: 'flex',
    alignItems: 'center',
  },
  numberInput: {
    margin: '0px 5px',
  },
  smallMarginLeft: {
    marginLeft: '5px',
  },
  smallMarginRight: {
    marginRight: '5px',
  },
  wrenchIcon: {
    color: Colors.GRAY_DARK,
  },
});
